import { getComponent } from "../../../../client";
import { WohaMedia } from "../../../../modules/content/components/WohaMedia/WohaMedia";
import { WohaRichText } from "../../../../modules/content/components/WohaRichText/WohaRichText";
import type { IFooterBlockProps } from "./FooterBlock.props";
export const FooterBlock = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  parentClass,
  secondaryLinks,
  socialLinks,
}: IFooterBlockProps) => {
  if (
    !logo &&
    !description &&
    !contact?.email &&
    !contact?.phone &&
    !contact?.address &&
    !navigationGroups?.length &&
    !socialLinks?.length &&
    !secondaryLinks?.length &&
    !copyright
  )
    return null;
  const Link = getComponent("Link");
  const linkClass =
    "inline-flex min-h-11 items-center text-sm text-white/70 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";
  return (
    <footer className={`${parentClass || ""} bg-charcoal text-white`}>
      <div className="mx-auto w-full max-w-[80rem] px-5 py-16 sm:px-8 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_2fr_0.8fr]">
          {" "}
          <div>
            {logo ? (
              <WohaMedia
                className="mb-6 h-10 w-44 rounded-none bg-white"
                imageClassName="object-contain object-left"
                media={logo}
                sizes="128px"
              />
            ) : null}
            {description ? (
              <WohaRichText
                className="max-w-sm text-base leading-7 text-white/70"
                content={description}
              />
            ) : null}
            {contact ? (
              <address className="mt-5 flex flex-col not-italic">
                {contact.email ? (
                  <a className={linkClass} href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                ) : null}
                {contact.phone ? (
                  <a className={linkClass} href={`tel:${contact.phone}`}>
                    {contact.phone}
                  </a>
                ) : null}
                {contact.address ? (
                  <p className="mt-2 text-sm leading-6 text-white/70">{contact.address}</p>
                ) : null}
              </address>
            ) : null}
          </div>
          {navigationGroups?.length ? (
            <nav aria-label="Footer" className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {navigationGroups.map((group, index) => (
                <div key={index}>
                  <h2 className="mb-3 text-sm font-semibold text-white">{group.header}</h2>
                  <ul>
                    {group.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          aria-label={link.accessibilityLabel || undefined}
                          className={linkClass}
                          href={link.href}
                          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                          target={link.openInNewTab ? "_blank" : undefined}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          ) : null}
          {socialLinks?.length ? (
            <div className="flex flex-wrap content-start gap-2">
              {socialLinks.map((item, index) => (
                <Link
                  aria-label={item.link.accessibilityLabel || undefined}
                  className="inline-flex min-h-11 items-center rounded-full border border-white/20 px-4 text-sm font-medium text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                  href={item.link.href}
                  key={index}
                  rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={item.link.openInNewTab ? "_blank" : undefined}
                >
                  {item.link.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-12 flex flex-col gap-3 border-t border-white/15 pt-6 text-sm text-white/50 sm:flex-row sm:items-center sm:justify-between">
          {secondaryLinks?.length ? (
            <div className="flex flex-wrap gap-x-5">
              {secondaryLinks.map((link, index) => (
                <Link
                  aria-label={link.accessibilityLabel || undefined}
                  className={linkClass}
                  href={link.href}
                  key={index}
                  rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={link.openInNewTab ? "_blank" : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ) : (
            <span />
          )}
          {copyright ? <p>{copyright}</p> : null}
        </div>
      </div>
    </footer>
  );
};
FooterBlock.displayName = "Block::FooterBlock";
